import ButtonMenu from "@/components/Forms/components/ButtonMenu";
import { customizeLabel } from "@/utils/labels/labelUtils";
import {
  Button,
  Loader,
  Modal,
  Option,
  SelectBox,
} from "@monolith-forensics/monolith-ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import type { Label } from "../labels.types";

export const SelectPrinterModal: React.FC<{
  show: boolean;
  onClose?: () => void;
  onCancel?: () => void;
  onSubmit?: () => void;
  data: any;
  label: Label;
}> = ({ show, onClose, onCancel, onSubmit, data, label }) => {
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [selectedPrinter, setSelectedPrinter] = useState<Option>();
  const DYMO = window.dymo.label.framework;

  const { data: printers, isFetched } = useQuery({
    queryKey: ["dymo:printers:list"],
    queryFn: async () => {
      await new Promise((resolve) => setTimeout(resolve, 500));
      return DYMO.getPrinters();
    },
    enabled: show,
  });

  const handleCancel = () => {
    onCancel?.();
  };

  const handleSubmit = () => {
    setSubmitting(true);

    customizeLabel(label.label_data, data)
      .then((customizedLabelXml) => {
        DYMO.openLabelXml(customizedLabelXml).print(selectedPrinter?.value);
        setSubmitting(false);
        onSubmit?.();
        onClose?.();
      })
      .catch((error) => {
        console.error(error);
        setSubmitting(false);
      });
  };

  const options: Option[] =
    printers?.map((printer: { name: string }) => ({
      label: printer.name,
      value: printer.name,
    })) || [];

  if (isFetched && printers?.length > 0 && !selectedPrinter) {
    setSelectedPrinter(options[0]); // Set the first printer as the default
  }

  return (
    <Modal open={show} style={{ width: 400 }}>
      <Modal.Title>Print Label</Modal.Title>
      <div
        style={{
          marginTop: 20,
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        {!isFetched && <Loader style={{ margin: "auto" }} />}
        {isFetched && options?.length === 0 && !submitting && (
          <div style={{ margin: "10px 0px" }}>
            No DYMO printers found. Please connect a DYMO printer and try again.
          </div>
        )}
        {submitting && (
          <div
            style={{
              margin: "auto",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
              gap: 10,
            }}
          >
            <Loader style={{ margin: "auto" }} />
            Printing Label...
          </div>
        )}
        {isFetched && options?.length > 0 && !submitting && (
          <SelectBox
            label="Select Printer"
            openOnFocus={false}
            data={options}
            onChange={(_, option) => setSelectedPrinter(option)}
            value={selectedPrinter}
          />
        )}
        <ButtonMenu>
          <Button
            size="xs"
            variant="subtle"
            onClick={handleCancel}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            size="xs"
            color="primary"
            variant="contained"
            onClick={handleSubmit}
            loading={submitting}
          >
            Print Label
          </Button>
        </ButtonMenu>
      </div>
    </Modal>
  );
};
